import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePreferences } from "../context/PreferencesContext";
import { Link } from "react-router-dom";
import {
  History,
  Loader2,
  PlayCircle,
  Palette,
  Check,
  Sun,
  Moon,
  Monitor,
  User,
  Trophy,
  Settings,
  Camera,
  Save,
  Lock,
} from "lucide-react";
import { useTheme } from "next-themes";
import { PIECE_THEME_ORDER, PIECE_THEMES } from "../game/pieceThemes";
import { PiecePreview } from "../components/Piece";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { displayName } from "../lib/displayName";

function initials(user) {
  const f = user?.first_name?.[0] || "";
  const l = user?.last_name?.[0] || "";
  if (f || l) return (f + l).toUpperCase();
  return (user?.name?.[0] || "F").toUpperCase();
}

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const { pieceTheme, setPieceTheme, setUiTheme, saving } = usePreferences();
  const { theme } = useTheme();
  const fileRef = useRef(null);

  const [history, setHistory] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [pseudo, setPseudo] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    api.get("/games/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
    api.get("/auth/me/ranking").then((r) => setRanking(r.data)).catch(() => setRanking(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name ?? user.name?.split(" ")[0] ?? "");
    setLastName(user.last_name ?? user.name?.split(" ").slice(1).join(" ") ?? "");
    setPseudo(user.pseudo ?? "");
    setAvatarPreview(user.avatar ?? null);
  }, [user]);

  if (!user) return null;

  const publicName = displayName(user);
  const hasPseudo = Boolean((user.pseudo || "").trim());

  const stats = user.stats || { wins: 0, losses: 0, draws: 0, elo: 1000, ranked_games: 0 };
  const total = stats.wins + stats.losses + stats.draws;
  const winRate = total > 0 ? Math.round((stats.wins / total) * 100) : 0;

  const onAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choisissez une image (JPEG, PNG, WebP).");
      return;
    }
    if (file.size > 200 * 1024) {
      toast.error("Image trop lourde (max 200 Ko).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        pseudo: pseudo.trim() || "",
        avatar: avatarPreview,
      });
      await refreshMe();
      toast.success("Profil mis à jour");
      setAvatarPreview(data.avatar ?? avatarPreview);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    setSavingPassword(true);
    try {
      await api.patch("/auth/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Mot de passe modifié");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 fade-up">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-8">
          <Avatar className="h-20 w-20 border-2 border-[color:var(--primary)]">
            <AvatarImage src={avatarPreview || undefined} alt={user.name} />
            <AvatarFallback className="text-xl font-bold bg-[color:var(--surface)] text-[color:var(--primary)]">
              {initials(user)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] mb-1">MON COMPTE</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="profile-name">
              {publicName}
            </h1>
            {hasPseudo && user.name && user.name !== publicName && (
              <p className="text-sm text-[color:var(--text-secondary)]">{user.name}</p>
            )}
            <p className="text-[color:var(--text-secondary)] text-sm mt-0.5">{user.email}</p>
            {!hasPseudo && (
              <p className="text-sm text-amber-500/90 mt-2">
                Choisissez un pseudo ci-dessous — c&apos;est le nom visible par les autres joueurs.
              </p>
            )}
            {user.rank_title && (
              <p className="text-sm text-[color:var(--primary)] mt-1">{user.rank_title} · {stats.elo} ELO</p>
            )}
          </div>
        </div>

        <Tabs defaultValue="compte" className="w-full">
          <TabsList className="glass w-full flex flex-wrap h-auto gap-1 p-1 mb-6">
            <TabsTrigger value="compte" className="flex-1 min-w-[100px] gap-2"><User size={14} /> Compte</TabsTrigger>
            <TabsTrigger value="classement" className="flex-1 min-w-[100px] gap-2"><Trophy size={14} /> Classement</TabsTrigger>
            <TabsTrigger value="historique" className="flex-1 min-w-[100px] gap-2"><History size={14} /> Historique</TabsTrigger>
            <TabsTrigger value="preferences" className="flex-1 min-w-[100px] gap-2"><Settings size={14} /> Préférences</TabsTrigger>
          </TabsList>

          <TabsContent value="compte" className="space-y-6">
            <section className="glass rounded-lg p-5 sm:p-6">
              <h2 className="text-lg font-bold mb-4">Informations personnelles</h2>
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex flex-col items-center gap-2">
                  <Avatar className="h-24 w-24 border border-[color:var(--border)]">
                    <AvatarImage src={avatarPreview || undefined} alt="" />
                    <AvatarFallback className="text-2xl">{initials({ ...user, first_name: firstName, last_name: lastName })}</AvatarFallback>
                  </Avatar>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarPick} />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                    <Camera size={14} className="mr-2" /> Changer la photo
                  </Button>
                  {avatarPreview && (
                    <button type="button" className="text-xs text-rose-400 hover:underline" onClick={() => setAvatarPreview(null)}>
                      Supprimer la photo
                    </button>
                  )}
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <Label htmlFor="pseudo">Pseudo</Label>
                    <Input
                      id="pseudo"
                      className="mt-1 bg-[color:var(--surface)] border-[color:var(--border)]"
                      value={pseudo}
                      onChange={(e) => setPseudo(e.target.value)}
                      placeholder="Ex. Tāne_Mata"
                      maxLength={20}
                      data-testid="profile-pseudo"
                    />
                    <p className="text-xs text-[color:var(--text-secondary)] mt-1">
                      Nom affiché en ligne, au classement et face à vos adversaires. 2–20 caractères.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first-name">Prénom</Label>
                      <Input
                        id="first-name"
                        className="mt-1 bg-[color:var(--surface)] border-[color:var(--border)]"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="last-name">Nom</Label>
                      <Input
                        id="last-name"
                        className="mt-1 bg-[color:var(--surface)] border-[color:var(--border)]"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input className="mt-1 bg-[color:var(--surface)]/50 border-[color:var(--border)]" value={user.email} disabled />
                  </div>
                  <Button className="btn-gold" onClick={saveProfile} disabled={savingProfile}>
                    {savingProfile ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
                    Enregistrer
                  </Button>
                </div>
              </div>
            </section>

            {user.has_password !== false && (
              <section className="glass rounded-lg p-5 sm:p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Lock size={18} /> Mot de passe</h2>
                <form className="space-y-4 max-w-md" onSubmit={savePassword}>
                  <div>
                    <Label htmlFor="current-pw">Mot de passe actuel</Label>
                    <Input
                      id="current-pw"
                      type="password"
                      autoComplete="current-password"
                      className="mt-1 bg-[color:var(--surface)] border-[color:var(--border)]"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-pw">Nouveau mot de passe</Label>
                    <Input
                      id="new-pw"
                      type="password"
                      autoComplete="new-password"
                      className="mt-1 bg-[color:var(--surface)] border-[color:var(--border)]"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={6}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-pw">Confirmer</Label>
                    <Input
                      id="confirm-pw"
                      type="password"
                      autoComplete="new-password"
                      className="mt-1 bg-[color:var(--surface)] border-[color:var(--border)]"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      minLength={6}
                      required
                    />
                  </div>
                  <Button type="submit" variant="outline" disabled={savingPassword}>
                    {savingPassword ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                    Modifier le mot de passe
                  </Button>
                </form>
              </section>
            )}
          </TabsContent>

          <TabsContent value="classement">
            <section className="glass rounded-lg p-5 sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold flex items-center gap-2"><Trophy size={18} className="text-[color:var(--primary)]" /> Mon classement</h2>
                <Link to="/leaderboard" className="text-sm text-[color:var(--primary)] hover:underline">Voir le top →</Link>
              </div>

              {ranking?.rank && (
                <div className="glass rounded-lg p-4 mb-6 border border-[color:var(--primary)]/30 flex items-center gap-4">
                  <div className="text-4xl font-extrabold text-[color:var(--primary)]">#{ranking.rank}</div>
                  <div>
                    <div className="font-semibold">sur {ranking.total} joueurs</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">{user.rank_title}</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "ELO", value: stats.elo, accent: true, testid: "stat-elo" },
                  { label: "Victoires", value: stats.wins, testid: "stat-wins" },
                  { label: "Défaites", value: stats.losses, testid: "stat-losses" },
                  { label: "% Victoire", value: `${winRate}%`, testid: "stat-winrate" },
                ].map((s) => (
                  <div key={s.label} className="rounded-lg border border-[color:var(--border)] p-4" data-testid={s.testid}>
                    <div className="text-xs tracking-[0.2em] text-[color:var(--text-secondary)] mb-2">{s.label.toUpperCase()}</div>
                    <div className={`text-2xl font-extrabold ${s.accent ? "text-[color:var(--primary)]" : ""}`}>{s.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-[color:var(--text-secondary)]">
                Égalités : {stats.draws}
                {stats.ranked_games != null && ` · Parties classées : ${stats.ranked_games}`}
              </div>
            </section>
          </TabsContent>

          <TabsContent value="historique">
            <div className="glass rounded-lg overflow-hidden" data-testid="profile-history">
              {history === null && (
                <div className="px-5 py-10 text-center text-sm flex items-center justify-center gap-2 text-[color:var(--text-secondary)]">
                  <Loader2 className="animate-spin" size={16} /> Chargement…
                </div>
              )}
              {history && history.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-[color:var(--text-secondary)]">
                  Pas encore de parties enregistrées. <Link to="/lobby" className="text-[color:var(--primary)] hover:underline">Jouer une partie</Link>
                </div>
              )}
              {history?.map((h) => (
                <div key={h.id} className="grid grid-cols-[72px_90px_1fr_100px_80px] gap-2 sm:gap-3 px-4 sm:px-5 py-3 border-b border-[color:var(--border)] last:border-0 text-sm items-center">
                  <div className="text-xs tracking-[0.15em] text-[color:var(--text-secondary)] uppercase">{h.mode}</div>
                  <div className={`font-semibold ${h.result === "win" ? "text-emerald-500" : h.result === "loss" ? "text-rose-500" : "text-[color:var(--text-secondary)]"}`}>
                    {h.result === "win" ? "Victoire" : h.result === "loss" ? "Défaite" : "Égalité"}
                  </div>
                  <div className="truncate">{h.opponent || "—"}</div>
                  <div className="text-right text-xs text-[color:var(--text-secondary)]">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString("fr-FR") : ""}
                  </div>
                  <div className="text-right">
                    {h.game_id ? (
                      <Link to={`/replay/${h.game_id}`} className="inline-flex items-center gap-1 text-xs text-[color:var(--primary)] hover:underline">
                        <PlayCircle size={14} /> Replay
                      </Link>
                    ) : (
                      <span className="text-xs text-[color:var(--text-secondary)]">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="preferences">
            <section className="glass rounded-lg p-5 sm:p-6" data-testid="profile-preferences">
              <h2 className="text-lg font-bold flex items-center gap-2 mb-2">
                <Palette size={18} className="text-[color:var(--primary)]" /> Thème des pions
              </h2>
              <p className="text-sm text-[color:var(--text-secondary)] mb-4">Coquillages &amp; cailloux sur le plateau.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {PIECE_THEME_ORDER.map((id) => {
                  const t = PIECE_THEMES[id];
                  const active = pieceTheme === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      disabled={saving}
                      onClick={async () => {
                        if (active) return;
                        try {
                          await setPieceTheme(id);
                          toast.success(`Thème « ${t.label} » appliqué.`);
                        } catch {
                          toast.error("Impossible de sauvegarder.");
                        }
                      }}
                      className={`rounded-lg p-4 text-left border transition ${
                        active ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10" : "border-[color:var(--border)] hover:border-[color:var(--primary)]/50"
                      }`}
                    >
                      <div className="font-semibold flex items-center gap-2">
                        {t.label}
                        {active && <Check size={14} className="text-[color:var(--primary)]" />}
                      </div>
                      <div className="flex items-center gap-3 mt-3">
                        <PiecePreview owner="A" themeId={id} size={32} />
                        <PiecePreview owner="B" themeId={id} size={32} />
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8">
                <p className="text-sm font-medium mb-3">Apparence de l&apos;interface</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "dark", label: "Sombre", icon: Moon },
                    { id: "light", label: "Clair", icon: Sun },
                    { id: "system", label: "Système", icon: Monitor },
                  ].map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      disabled={saving}
                      onClick={async () => {
                        try {
                          await setUiTheme(id);
                          toast.success(`Thème ${label.toLowerCase()} activé`);
                        } catch {
                          toast.error("Impossible de sauvegarder le thème");
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition ${
                        theme === id ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10" : "border-[color:var(--border)]"
                      }`}
                    >
                      <Icon size={16} /> {label}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
